/**
 * Classic Menu Template
 * Elegant traditional menu design
 */
import React from "react";
import { ChefHat, Salad, Coffee, IceCream, Leaf, WheatOff, AlertTriangle } from "lucide-react";
import { ALLERGENE_TRANSLATIONS, FTU_LOGO_URL } from "../constants";

export function ClassicTemplate({ 
  menuRef,
  generatedMenu, 
  formules, 
  menuLanguage, 
  showPrix, 
  showAllergenes 
}) {
  if (!generatedMenu) return null;

  return (
    <div
      ref={menuRef}
      className="relative min-h-[600px] p-8"
      style={{
        backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${generatedMenu.background_url})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      {/* Classic Menu Content */}
      <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur rounded-xl shadow-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
            {generatedMenu.titre}
          </h1>
          {generatedMenu.sous_titre && (
            <p className="text-lg text-muted-foreground italic">
              {generatedMenu.sous_titre}
            </p>
          )}
          <div className="w-24 h-1 bg-primary mx-auto mt-4 rounded-full" />
        </div>
        
        {/* Formules */}
        {formules.length > 0 && (
          <div className="mb-8 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <h2 className="text-xl font-heading font-semibold text-center mb-4 text-primary">
              {menuLanguage === "en" ? "Set Menus" : "Nos Formules"}
            </h2>
            <div className="space-y-3">
              {formules.map((formule, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div>
                    <span className="font-semibold">{formule.nom}</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      ({formule.items.map(i => 
                        menuLanguage === "en" 
                          ? (i === "entree" ? "Starter" : 
                             i === "plat" ? "Main" : 
                             i === "dessert" ? "Dessert" : "Drink")
                          : (i === "entree" ? "Entrée" : 
                             i === "plat" ? "Plat" : 
                             i === "dessert" ? "Dessert" : "Boisson")
                      ).join(" + ")})
                    </span>
                  </div>
                  <span className="font-bold text-primary text-lg">{formule.prix} €</span>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Sections */}
        {generatedMenu.sections?.map((section, sIdx) => (
          <div key={sIdx} className="mb-6">
            <h2 className="text-xl font-heading font-semibold text-secondary mb-4 flex items-center gap-2">
              {section.type === "entree" && <Salad className="w-5 h-5 text-green-600" />}
              {section.type === "plat" && <ChefHat className="w-5 h-5 text-orange-600" />}
              {section.type === "dessert" && <IceCream className="w-5 h-5 text-pink-600" />}
              {section.type === "boisson" && <Coffee className="w-5 h-5 text-blue-600" />}
              {menuLanguage === "en" 
                ? (section.type === "entree" ? "Starters" :
                   section.type === "plat" ? "Main Courses" :
                   section.type === "dessert" ? "Desserts" : "Beverages")
                : section.titre}
            </h2>
            <div className="space-y-3">
              {section.plats?.map((plat, pIdx) => (
                <div key={pIdx} className="flex justify-between items-start border-b border-dashed border-gray-200 pb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{plat.nom}</span>
                      {plat.vegetarien && <Leaf className="w-4 h-4 text-green-600" title={menuLanguage === "en" ? "Vegetarian" : "Végétarien"} />}
                      {plat.vegan && <Leaf className="w-4 h-4 text-green-700" title="Vegan" />}
                      {plat.sans_gluten && <WheatOff className="w-4 h-4 text-amber-600" title={menuLanguage === "en" ? "Gluten-free" : "Sans gluten"} />}
                    </div>
                    {plat.description && (
                      <p className="text-sm text-muted-foreground">{plat.description}</p>
                    )}
                    {showAllergenes && plat.allergenes?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        {menuLanguage === "en" 
                          ? plat.allergenes.map(a => ALLERGENE_TRANSLATIONS[a] || a).join(", ")
                          : plat.allergenes.join(", ")}
                      </p>
                    )}
                  </div>
                  {showPrix && (
                    <div className="ml-4 flex items-center gap-1">
                      {plat.original_prix && plat.original_prix !== plat.prix && (
                        <span className="text-xs text-gray-400 line-through">{plat.original_prix.toFixed(2)} €</span>
                      )}
                      <span className={`font-bold ${plat.original_prix && plat.original_prix !== plat.prix ? 'text-orange-600' : 'text-primary'}`}>
                        {plat.prix.toFixed(2)} €
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        
        {/* Footer */}
        <div className="text-center mt-8 pt-4 border-t">
          {/* Logo Food Truck United */}
          <img 
            src={FTU_LOGO_URL} 
            alt="Food Truck United" 
            className="h-10 mx-auto mb-3 opacity-80"
          />
          <p className="text-sm text-muted-foreground">
            {generatedMenu.food_truck?.nom}
          </p>
          {showAllergenes && (
            <p className="text-xs text-muted-foreground mt-2">
              {menuLanguage === "en" 
                ? "Please inform us of any allergies"
                : "Pour toutes allergies, merci de nous consulter"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClassicTemplate;
